'use client'

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAuth } from './AuthContext'
import { clearCache } from '../helpers/mediaCache'

const SessionFeedContext = createContext(null)

/**
 * Mounted at the app root (see app/layout.jsx) so the result feed survives navigating
 * away from /generate and back; persistence is per session (a full reload starts fresh).
 * Only `bFeed` is reactive; the value is memoized on it so consumers don't re-render on
 * unrelated auth updates (e.g. a gem-count change).
 */
export function SessionFeedProvider({ children }) {
  const { user } = useAuth()

  const [bFeed, setBFeed] = useState([])
  const bIdRef = useRef(0)
  const lastImageBatchRef = useRef(null)
  const lastVideoBatchRef = useRef(null)
  const bPendingIdRef = useRef(null)
  const bPendingSourceRef = useRef(null)
  // What the in-flight video extends ({ parentId, parentVersion, selection, replacesId }),
  // or null for a plain generation. Read when the result lands, so the card can offer Redo.
  const bVideoExtendRef = useRef(null)
  const prevUidRef = useRef(undefined)

  useEffect(() => {
    // Account switch / sign-out clears the feed so results don't leak across users.
    // Dedup refs are intentionally NOT reset, so a stale imageSrc/videoSrc still in
    // CreateContext isn't re-added to the freshly-cleared feed.
    const prevUid = prevUidRef.current
    prevUidRef.current = user?.uid
    setBFeed([])
    bPendingIdRef.current = null
    bPendingSourceRef.current = null
    bVideoExtendRef.current = null
    // Also wipe the short-term media cache — but ONLY when leaving a real previous
    // account (switch or sign-out). Not on initial sign-in, so a fresh reload can
    // still hydrate that user's own recent results.
    if (prevUid && prevUid !== user?.uid) clearCache(prevUid)
  }, [user?.uid])

  const value = useMemo(
    () => ({
      bFeed,
      setBFeed,
      bIdRef,
      lastImageBatchRef,
      lastVideoBatchRef,
      bPendingIdRef,
      bPendingSourceRef,
      bVideoExtendRef,
    }),
    [bFeed],
  )

  return (
    <SessionFeedContext.Provider value={value}>
      {children}
    </SessionFeedContext.Provider>
  )
}

export function useSessionFeed() {
  const ctx = useContext(SessionFeedContext)
  if (!ctx) {
    throw new Error('useSessionFeed must be used within <SessionFeedProvider>')
  }
  return ctx
}
